import { useState, useCallback, useEffect, useContext } from "react";
import useMountStatus from '../../../../hooks/useMountStatus';
import { useNavigate } from "react-router";

import { DashboardContext } from "../../../../context/DashboardContext";
import { NO_ACCESS_TYPES, PERMISSIONS } from "../../../../utils/constants";
import { EMPTY_FIELDS, PASSWORDS_MISMATCH } from '../../../../utils/constants';
import { addUser, getOrganisationUsers } from "../../../../utils/api";

import { UserAddIcon, LockClosedIcon, UserIcon, AtSymbolIcon } from "@heroicons/react/solid";

import Alert, { ALERT_TYPE } from "../../../Auth/Alert";
import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import Input from '../../../Auth/Input';
import Button from "../../../SharedComponents/Button";
import AccessControl from "../../../SharedComponents/AccessControl";

function AddUser() {
    const context = useContext(DashboardContext);
    const navigate = useNavigate();
    
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [organisationUsers, setOrganisationUsers] = useState([]);
    const [selectedAccessUser, setSelectedAccessUser] = useState();
    const [alert, setAlert] = useState('');
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);

    const unmounted = useMountStatus();

    const isInputValid = () => {
        if (password === "" || confirmPassword === "") {
            setAlert(EMPTY_FIELDS);

            return false
        }

        if (password !== confirmPassword) {
            setAlert(PASSWORDS_MISMATCH);

            return false
        }

        return true
    }

    const handleSuccessfulAddUser = async () => {
        setAlert("You've successfully added a user");
        setAlertType(ALERT_TYPE.SUCCESS);
    }

    const handleAddUser = async () => {
        if (isInputValid()) {
            try {
                setSubmitLoading(true);
                await addUser(
                    email,
                    confirmPassword,
                    firstName,
                    lastName,
                    selectedAccessUser ? selectedAccessUser.identifier : null,
                );

                await handleSuccessfulAddUser();

                setSubmitLoading(false)
            } catch (err) {
                setSubmitLoading(false);

                setAlertType(ALERT_TYPE.ERROR);
                setAlert(err.message);
            }
        }
    }

    const fetchOrganisationUsers = useCallback(async () => {
        setUsersLoading(true);
        try {
            const organisationUsers = await getOrganisationUsers();
            if (unmounted) return;
            setOrganisationUsers(organisationUsers);
        } catch (err) {
            if (unmounted) return;
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setUsersLoading(false);
    }, [unmounted]);

    useEffect(() => {
        fetchOrganisationUsers().then();
    }, [fetchOrganisationUsers]);

    useEffect(() => {
        if (!context.permissionsLoading && !context.userPermissions.includes(PERMISSIONS.ACCESS_SETUP_USER_PAGE)) {
            navigate("/no-permission");
        }
    }, [context.permissionsLoading, context.userPermissions, navigate]);

    return (
        <AccessControl requiredPermission={PERMISSIONS.CREATE_USER_ACCOUNTS} noAccessType={NO_ACCESS_TYPES.REDIRECT}>
            <DashboardContainer title={"Add User"} titleIcon={<UserAddIcon className="block h-5 w-5 mr-2"/>}>
                <div className="grid grid-flow-row gap-4">
                    <div className="md:col-span-1">
                        <div>
                            <h3 className="text-md font-medium leading-6 text-indigo-900">
                                User details
                            </h3>
                            <p className="text-sm text-gray-600">
                                Please complete the form below to add a User
                            </p>
                        </div>
                    </div>
                    <div className="md:col-span-2">
                        <form action="#" method="POST">
                            <Alert
                                text={alert}
                                type={alertType}
                                active={alert}
                                close={() => setAlert("")}
                            />
                            <div className="shadow-sm overflow-hidden rounded-md">
                                <div className="grid grid-flow-row gap-4 md:gap-6 p-4 bg-white">
                                    <div className={"grid grid-flow-row md:grid-cols-6 gap-4 md:gap-6"}>
                                        <div className={
                                            "col-span-3 grid grid-flow-row gap-4 md:gap-6 lg:col-span-3 " +
                                            "sm:col-span-6"
                                        }>
                                            <div className={"grid grid-flow-row md:grid-flow-col gap-4 md:gap-6"}>
                                                <Input
                                                    optional
                                                    type="text"
                                                    name="First name"
                                                    id="firstname"
                                                    placeholder={"First name"}
                                                    onChange={setFirstName}
                                                    Icon={<UserIcon className="h-4 w-4 text-gray-500"/>}
                                                />
                                                <Input
                                                    optional
                                                    type="text"
                                                    name="Last name"
                                                    id="lastname"
                                                    placeholder={"Last name"}
                                                    onChange={setLastName}
                                                    Icon={<UserIcon className="h-4 w-4 text-gray-500"/>}
                                                />
                                            </div>
                                            <Input
                                                id="email"
                                                type="email"
                                                name="Email"
                                                placeholder="Enter email"
                                                onChange={setEmail}
                                                Icon={<AtSymbolIcon className="h-4 w-4 text-gray-500"/>}
                                            />
                                        </div>
                                        <div className={
                                            "col-span-3 grid grid-flow-row gap-4 md:gap-6 lg:col-span-3 " +
                                            "sm:col-span-6"
                                        }>
                                            <Input
                                                id="password"
                                                type="password"
                                                name="Password"
                                                placeholder="Enter password"
                                                onChange={setPassword}
                                                Icon={<LockClosedIcon className="h-4 w-4 text-gray-500"/>}
                                            />
                                            <Input
                                                id="confirmPassword"
                                                type="password"
                                                name="Confirm Password"
                                                placeholder="Confirm password"
                                                onChange={setConfirmPassword}
                                                Icon={<LockClosedIcon className="h-4 w-4 text-gray-500"/>}
                                            />
                                        </div>
                                    </div>
                                    {process.env.REACT_APP_FEATURE_ENABLEMENT_LEVEL <= 0 &&
                                        <>
                                            <div className="grid grid-flow-col gap-4 md:gap-6">
                                                <div>
                                                    <label
                                                        htmlFor="first-name"
                                                        className="block text-sm font-medium text-indigo-900">
                                                        Access
                                                    </label>
                                                    <div className={"mt-2"}>
                                                        <label
                                                            htmlFor="first-name"
                                                            className="block text-sm text-blue-gray-500">
                                                            Copy access from:
                                                        </label>
                                                        <select
                                                            id="organisationUsers"
                                                            name="organisationUsers"
                                                            disabled={usersLoading}
                                                            className={
                                                                "mt-2 bg-blue-gray-50 border " +
                                                                "border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 " +
                                                                "text-left focus:outline-none focus:ring-1 " +
                                                                "focus:ring-primary-600 focus:border-primary-600 text-sm"
                                                            }
                                                            onChange={
                                                                e => setSelectedAccessUser(organisationUsers
                                                                    .filter(organisationUser =>
                                                                        (organisationUser.emailAddress === e.target.value))[0])
                                                            }>
                                                            {usersLoading ?
                                                                <option value="" className={"italic"}>
                                                                    Loading organisation users..
                                                                </option>
                                                                :
                                                                <option value="no_access">Do not copy access</option>
                                                            }


                                                            {organisationUsers.map((user, index) => (
                                                                <option key={index} value={user.emailAddress}
                                                                        className={"mt-1"}>
                                                                    {user.name}
                                                                </option>
                                                            ))
                                                            }

                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                            {usersLoading ?
                                                <div className={"grid grid-flow-col gap-4"}>
                                                    <div>
                                                        <div
                                                            className={"animate-pulse px-4 py-3 h-5 bg-blue-gray-200 rounded-lg h-4 w-2/4"}/>
                                                        <div
                                                            className={"animate-pulse px-4 py-3 h-5 bg-blue-gray-200 rounded-lg w-full h-9 mt-2"}/>
                                                    </div>
                                                    <div>
                                                        <div
                                                            className={"animate-pulse px-4 py-3 h-5 bg-blue-gray-200 rounded-lg h-4 w-2/4"}/>
                                                        <div
                                                            className={"animate-pulse px-4 py-3 h-5 bg-blue-gray-200 rounded-lg w-full h-9 mt-2"}/>
                                                    </div>
                                                </div>
                                                :
                                                selectedAccessUser ?
                                                    <div className={"grid grid-flow-col gap-4"}>
                                                        <div
                                                            className={"bg-blue-gray-50 p-3 rounded-lg border-blue-gray-200 border"}>
                                                            <div className="flex justify-between">
                                                                <label htmlFor="first-name"
                                                                       className="block text-sm font-medium text-indigo-900">
                                                                    Direct Permissions to be copied
                                                                </label>
                                                                <span
                                                                    className={"text-xs text-blue-gray-400"}>{selectedAccessUser && selectedAccessUser.permissions.length} Direct Permissions</span>
                                                            </div>
                                                            <div className={"grid grid-cols-2 gap-2 mt-4"}>
                                                                {selectedAccessUser ?
                                                                    (selectedAccessUser.permissions.length !== 0 ?

                                                                            selectedAccessUser.permissions.map((permission, index) => (
                                                                                <div className={
                                                                                    "text-xs font-medium text-secondary-600 bg-secondary-100 p-1 px-2 w-max " +
                                                                                    "rounded-full"
                                                                                }
                                                                                     key={index}
                                                                                >
                                                                                    {permission}
                                                                                </div>
                                                                            ))
                                                                            :
                                                                            selectedAccessUser.permissions.length === 0 &&
                                                                            <span
                                                                                className={"text-sm text-blue-gray-400 col-span-full"}>
                                                            {selectedAccessUser.name} does not have any direct permissions assigned
                                                        </span>
                                                                    )
                                                                    :
                                                                    <span className={"text-sm text-blue-gray-400"}>No user has been selected</span>
                                                                }
                                                            </div>
                                                        </div>
                                                        <div
                                                            className={"bg-blue-gray-50 p-3 rounded-lg border-blue-gray-200 border"}>
                                                            <div className="flex justify-between">
                                                                <label htmlFor="first-name"
                                                                       className="block text-sm font-medium text-indigo-900">
                                                                    User Groups to be copied
                                                                </label>
                                                                <span
                                                                    className={"text-xs text-blue-gray-400"}>{selectedAccessUser && selectedAccessUser.userGroups.length} User Groups</span>
                                                            </div>
                                                            <div className={"grid grid-cols-2 gap-2 mt-4"}>
                                                                {selectedAccessUser ?
                                                                    (selectedAccessUser.userGroups.length !== 0 ?
                                                                            selectedAccessUser.userGroups.map((userGroup, index) => (
                                                                                <div className={
                                                                                    "text-xs font-medium text-primary-600 bg-primary-100 p-1 px-2 w-max " +
                                                                                    "rounded-full"
                                                                                }
                                                                                     key={index}
                                                                                >
                                                                                    {userGroup}
                                                                                </div>
                                                                            ))
                                                                            :
                                                                            selectedAccessUser.userGroups.length === 0 &&
                                                                            <span
                                                                                className={"text-sm text-blue-gray-400 col-span-full"}>
                                                            {selectedAccessUser.name} does not have any User Groups assigned
                                                            </span>
                                                                    )
                                                                    :
                                                                    <span className={"text-sm text-blue-gray-400"}>No user has been selected</span>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                    :
                                                    <div className={
                                                        "text-sm w-full mx-auto rounded-md px-3 py-3 info bg-amber-100 " +
                                                        "text-amber-700 align-center justify-center"
                                                    }>
                                                        You will be able to add permissions, user groups and
                                                        applications after
                                                        user has been created
                                                    </div>
                                            }
                                        </>
                                    }
                                </div>
                                <div className={
                                    "p-4 bg-gradient-to-l from-primary-50 to-secondary-50 flex items-center justify-end " +
                                    "text-right"
                                }>
                                    <Button
                                        text="Add User"
                                        icon={<UserAddIcon className="block h-4 w-4 mr-2"/>}
                                        iconPositionLeft
                                        onClick={handleAddUser}
                                        loading={submitLoading}
                                        extraStyling={
                                            "text-white focus:ring-inset focus:ring-1 bg-secondary-500 " +
                                            "hover:bg-secondary-600 focus:outline-none focus:ring-secondary-500"
                                        }
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </DashboardContainer>
        </AccessControl>
    );
}

export default AddUser;
